import { debounce, isString } from 'lodash';
import { useMemo, useState, MouseEvent, useCallback } from 'react';

import { Box } from '@mui/system';
import {
  Card,
  Badge,
  Stack,
  Button,
  Select,
  Tooltip,
  MenuItem,
  useTheme,
  CardProps,
  TextField,
  IconButton,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
  InputAdornment,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { BooleanManager } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { TenantToColumnMap } from 'src/utils/jobs-kanban';

import { useTranslate } from 'src/locales';
import { rootApi } from 'src/services/rootApi';
import { jobsApi } from 'src/services/jobs/jobs.service';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { isOrganisationTenant } from 'src/services/auth/auth.utils';
import { User } from 'src/services/organisation/organisation.types';
import { showImportJobModal } from 'src/store/slices/jobs/jobsSlice';
import { JobStatus, JobFilters, RoleStatus } from 'src/services/jobs/jobs.types';

import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { TenantType } from 'src/types/enums';
import TableSorter from 'src/components/sorter/table-sorter';
import { SortOrder } from 'src/services/common.types';

type Props = {
  showTable?: BooleanManager;
  filters: JobFilters;
  onFilters: (name: keyof JobFilters, value: any) => void;
  onSortChange?: (sort: { sort_by: string; sort_order: SortOrder }) => void;
  clearFilters: VoidFunction;
  showCreate?: boolean;
  showImport?: boolean;
} & CardProps;

export default function JobsTableToolbar({
  onFilters,
  onSortChange,
  showTable,
  filters,
  clearFilters,
  showCreate = true,
  showImport = true,
  sx,
}: Props) {
  const popover = usePopover();

  const settings = useSettingsContext();

  const actionsControl = usePopover();

  const { t } = useTranslate();

  const router = useRouter();

  const theme = useTheme();

  const tenant = useOrgTenant();

  const dispatch = useAppDispatch();

  const users = useAppSelector((state) => state?.data?.users);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [selectedJobStatus, setSelectedJobStatus] = useState<string>('');

  const [selectedStage, setSelectedStage] = useState<string>('');

  const [selectedRoleStatus, setSelectedRoleStatus] = useState<RoleStatus | null>(null);

  const [searchValue, setSearchValue] = useState('');

  const { isFetching /* fulfilledTimeStamp */ } = jobsApi.endpoints.getMyActiveJobs.useQueryState();

  const onSelectUser = (user: User | null) => {
    setSelectedUser(user);
    onFilters('assigned_account', user?.id);
  };

  const applicationStages = TenantToColumnMap[tenant];

  const onClearSearch = () => {
    setSearchValue('');

    onFilters('search_query', undefined);
  };

  const onClearFilters = () => {
    onClearSearch();

    setSelectedUser(null);
    setSelectedJobStatus('');
    setSelectedStage('');
    setSelectedRoleStatus(null);

    clearFilters();
    popover.onClose();
  };

  // eslint-disable-next-line
  const onSetSearchFilter = useCallback(
    debounce((value: string) => {
      onFilters('search_query', value !== '' ? value : undefined);
    }, 500),
    [onFilters]
  );

  const onSetSelectedJobStatus = useCallback(
    (jobStatus: string) => {
      setSelectedJobStatus(jobStatus);
      onFilters('job_status', jobStatus);
    },
    [onFilters]
  );

  const onSelectApplicationStage = useCallback(
    (stage: string) => {
      onFilters('application_status', stage);
      setSelectedStage(stage);
    },
    [onFilters]
  );

  const onSelectRoleStatus = useCallback(
    (status: RoleStatus) => {
      onFilters('role_status', status);
      setSelectedRoleStatus(status);
    },
    [onFilters]
  );

  const onSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSetSearchFilter(event.target.value);
      setSearchValue(event.target.value);
    },
    [onSetSearchFilter]
  );

  const onImportJobClicked = useCallback((event: MouseEvent<any>) => {
    event.stopPropagation();
    event.preventDefault();

    dispatch(showImportJobModal());
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const refreshData = () => {
    dispatch(rootApi.util.invalidateTags(['Job']));
  };

  const disableReset = useMemo(() => Object.values(filters).every((value) => !value), [filters]);

  // const lastUpdated = useMemo(
  //   () => (fulfilledTimeStamp ? new Date(fulfilledTimeStamp) : undefined),
  //   [fulfilledTimeStamp]
  // );

  const isHighContrast = settings.themeContrast === 'bold';
  const jobFilterCount = useMemo(() => Object.keys(filters)?.filter((filterKey: string) => !['page', 'per_page'].includes(filterKey) && !!(filters as any)[filterKey]).length, [filters]);

  const renderFilters = (
    <>
      <Box>
        <TextField
          value={searchValue}
          placeholder={t('jobs.toolbar.search.placeholder')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="bi:search" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onClearSearch}>
                  <Iconify icon="iconamoon:close-bold" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ xs: { pl: 0 }, sm: { pl: 0 }, md: { pl: 1 }, minWidth: 120, maxWidth: 200 }}
          onChange={onSearch}
        />
      </Box>

      {isOrganisationTenant(tenant) && (
        <Box>
          <Autocomplete
            options={users as User[]}
            value={selectedUser}
            onChange={(_event, value) => onSelectUser(value)}
            renderOption={({ key, ...props }, option) => (
              <li key={key} {...props}>
                <Stack direction="column">
                  <Typography variant="body1">
                    {`${option.first_name} ${option.last_name}`}
                  </Typography>
                  <Typography variant="body2">{option.email}</Typography>
                </Stack>
              </li>
            )}
            getOptionLabel={(option) =>
              option && !isString(option) ? `${option.first_name} ${option.last_name}` : ''
            }
            isOptionEqualToValue={(option: User, value: User) => option.id === value.id}
            getOptionKey={(option) => (option as User).id as string}
            renderInput={(params) => (
              <TextField {...params} label={t('jobs.toolbar.filters.users')} />
            )}
            sx={{
              minWidth: 200,
            }}
          />
        </Box>
      )}

      <Box>
        <FormControl fullWidth>
          <InputLabel id="job-status-select-label">
            {t('jobs.toolbar.filters.job_status')}
          </InputLabel>
          <Select
            id="job-status-select"
            value={selectedJobStatus}
            label="Job Status"
            sx={{
              minWidth: 150,
            }}
            onChange={(event) => onSetSelectedJobStatus(event.target.value as string)}
            placeholder="Job Status"
          >
            <MenuItem value="">{t('common.none')}</MenuItem>
            <MenuItem value={JobStatus.ACTIVE}>{t('enums.job_status.active')}</MenuItem>
            {tenant !== TenantType.Candidate && (
              <MenuItem value={JobStatus.DRAFT}>{t('enums.job_status.draft')}</MenuItem>
            )}
            {tenant !== TenantType.Candidate && (
              <MenuItem value={JobStatus.UNAPPROVED}>{t('enums.job_status.unapproved')}</MenuItem>
            )}
            <MenuItem value={JobStatus.ARCHIVED}>{t('enums.job_status.archived')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl fullWidth>
          <InputLabel>{t('jobs.toolbar.filters.stage')}</InputLabel>
          <Select
            id="job-status-select"
            value={selectedStage}
            label="Job Status"
            sx={{
              minWidth: 150,
            }}
            onChange={(event) => onSelectApplicationStage(event.target.value as string)}
            placeholder="Job Status"
          >
            <MenuItem value="">{t('common.none')}</MenuItem>
            {applicationStages.map((stage) => (
              <MenuItem key={stage} value={stage}>
                {t(`enums.application_stage.${stage}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl fullWidth>
          <InputLabel>{t('jobs.toolbar.filters.role_status')}</InputLabel>
          <Select
            id="role-status-select"
            value={selectedRoleStatus}
            label="Role Status"
            sx={{
              minWidth: 150,
            }}
            onChange={(event) => onSelectRoleStatus(event.target.value as RoleStatus)}
            placeholder="Role Status"
          >
            <MenuItem value="">{t('common.none')}</MenuItem>
            {Object.values(RoleStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {t(`enums.role_status.${status}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Tooltip title={t('jobs.table.clear')}>
        <IconButton
          size="large"
          onClick={onClearFilters}
          aria-label="Clear job filters"

          disabled={disableReset}
        >
          <Badge badgeContent={jobFilterCount} color="error">
            <Iconify icon="material-symbols:cancel" />
          </Badge>
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <Card
      sx={{
        ...sx,
        borderColor: !isHighContrast ? theme.palette.background.neutral : 'none',
        borderWidth: !isHighContrast ? 1 : 0,
        borderStyle: 'solid',
      }}
      elevation={10}
    >
      <Stack
        spacing={2}
        alignItems={{ xs: 'flex-end', md: 'center' }}
        direction={{
          xs: 'column',
          md: 'row',
        }}
        sx={{
          p: 2.5,
          pr: { xs: 2.5, md: 2 },
          width: '100%',
        }}
      >
        <Stack
          sx={{ width: '100%' }}
          direction="row"
          justifyContent={{ xs: 'flex-end', lg: 'space-between' }}
          spacing={1}
          flexGrow={1}
        >
          <Stack
            direction="row"
            gap={1}
            sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' } }}
          >
            {renderFilters}
          </Stack>

          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
          >
            <Button
              variant="text"
              sx={{ display: { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none' } }}
              startIcon={<Iconify icon="mdi:filter" />}
              onClick={popover.onOpen}
            >
              {t('common.filter')}
            </Button>

            <Box sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' } }}>
              {
                onSortChange && (
                  <TableSorter
                    sortOptions={[
                      { key: 'title', label: t('jobs.sorters.title') },
                      { key: 'last_application_status', label: t('jobs.sorters.last_application_status') },
                      { key: 'vacancies', label: t('jobs.sorters.vacancies') },
                      { key: 'created_at', label: t('jobs.sorters.created_at') },
                      { key: 'updated_at', label: t('jobs.sorters.updated_at') },
                    ]}
                    onChange={(sort) => onSortChange?.(sort)}
                  />
                )
              }
            </Box>

            {tenant !== TenantType.Candidate && (
              <Tooltip title={`${t('jobs.table.refresh')}`}>
                <IconButton size="large" onClick={refreshData}>
                  <Iconify icon={isFetching ? 'eos-icons:bubble-loading' : 'mdi:reload'} />
                </IconButton>
              </Tooltip>
            )}

            {showTable && (
              <Tooltip
                title={
                  showTable.value ? t('jobs.table.toggle.kanban') : t('jobs.table.toggle.table')
                }
              >
                <span>
                  <IconButton size="large" onClick={showTable.onToggle}>
                    <Iconify icon={showTable.value ? 'bi:kanban-fill' : 'bi:grid-fill'} />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {
              (showCreate || showImport) && (
                <Stack direction="row" gap={1}>
                  <Button
                    size="medium"
                    onClick={actionsControl.onOpen}
                    startIcon={<Iconify icon="solar:hamburger-menu-line-duotone" />}
                    variant="outlined"
                    sx={{ textWrap: 'nowrap' }}
                  >
                    {t('common.actions')}
                  </Button>
                </Stack>
              )
            }

          </Stack>
        </Stack>
      </Stack>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="right-top">
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ p: 2 }}>
          {renderFilters}
        </Stack>
      </CustomPopover>

      <CustomPopover
        open={actionsControl.open}
        onClose={actionsControl.onClose}
        arrow="top-right"
      >
        <Stack>
          {showCreate && (
            <MenuItem
              onClick={() => router.push(paths.dashboard.jobs.create)}
            >
              <Iconify icon="material-symbols:add" />
              <Typography variant="body2">
                {t('jobs.toolbar.add_job')}
              </Typography>
            </MenuItem>
          )}

          {showImport && (
            <MenuItem
              onClick={onImportJobClicked}
            >
              <Iconify icon="lets-icons:import-duotone-line" />
              <Typography variant="body2">
                {t('jobs.toolbar.import_job')}
              </Typography>
            </MenuItem>
          )}
        </Stack>
      </CustomPopover >
    </Card >
  );
}
